/**
 * PanelBrowserDialog Component
 *
 * Dialog for browsing and selecting a clinic-configured lab panel.
 * Uses the same data source as the "Available Panels" tab on the Lab Orders
 * page, so the appointment wizard and the lab orders page stay in sync.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Loader2,
  TestTube,
  DollarSign,
  ChevronRight,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { getPanels, LabPanel } from '@/services/labService';
import { cn } from '@/lib/utils';

interface PanelBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPanelId?: string | null;
  onPanelSelected: (panel: LabPanel) => void;
}

const getMethodLabel = (m?: string) => {
  if (m === 'at_home_phlebotomy') return 'At Home Phlebotomy';
  if (m === 'walk_in_test' || m === 'walk_in') return 'Walk-in';
  return m || '';
};

const PanelBrowserDialog: React.FC<PanelBrowserDialogProps> = ({
  open,
  onOpenChange,
  selectedPanelId,
  onPanelSelected,
}) => {
  const [panels, setPanels] = useState<LabPanel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('all');

  useEffect(() => {
    if (open) {
      loadPanels();
    } else {
      setSearchQuery('');
      setMethodFilter('all');
    }
  }, [open]);

  const loadPanels = async () => {
    try {
      setLoading(true);
      const data = await getPanels();
      setPanels(data);
    } catch (error: any) {
      console.error('Error loading panels:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to load panels',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredPanels = useMemo(() => {
    let filtered = panels;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        panel =>
          panel.name.toLowerCase().includes(query)
          || panel.description?.toLowerCase().includes(query)
          || (panel.markers || []).some(m => m.name.toLowerCase().includes(query)),
      );
    }
    if (methodFilter !== 'all') {
      filtered = filtered.filter(panel => {
        const m = panel.collectionMethod || panel.collection_method;
        return m === methodFilter;
      });
    }
    return filtered;
  }, [panels, searchQuery, methodFilter]);

  const handleSelect = (panel: LabPanel) => {
    onPanelSelected(panel);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Browse Lab Panels</DialogTitle>
          <DialogDescription>
            Select a panel from the clinic's available lab panels
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search panels..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Collection Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="walk_in_test">Walk-in</SelectItem>
                <SelectItem value="at_home_phlebotomy">At-home phlebotomy</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto border rounded-lg p-3">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : filteredPanels.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <TestTube className="h-12 w-12 text-slate-300 mb-2" />
                <p className="text-slate-500">
                  {searchQuery || methodFilter !== 'all'
                    ? 'No panels match your filters'
                    : 'No panels available'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {filteredPanels.map(panel => (
                  <PanelOptionCard
                    key={panel.id}
                    panel={panel}
                    isSelected={panel.id === selectedPanelId}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PanelOptionCard: React.FC<{
  panel: LabPanel;
  isSelected: boolean;
  onSelect: (panel: LabPanel) => void;
}> = ({ panel, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const markers = panel.markers || [];
  const price = panel.patientPrice ?? panel.price;
  const method = panel.collectionMethod || panel.collection_method;

  return (
    <Card
      className={cn(
        'border border-slate-200 transition-shadow hover:shadow-sm',
        isSelected && 'ring-2 ring-brand-teal border-brand-teal',
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <TestTube className="h-3.5 w-3.5 text-brand-teal flex-shrink-0" />
              <span className="text-sm font-medium text-slate-900">{panel.name}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {method && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {getMethodLabel(method)}
                </Badge>
              )}
              {(panel.fastingRequired || panel.fasting_required) && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-200">
                  Fasting
                </Badge>
              )}
              {panel.turnaroundDays && (
                <span className="text-[10px] text-slate-400">~{panel.turnaroundDays}d</span>
              )}
            </div>
            {panel.description && (
              <p className="mt-1.5 text-xs text-slate-500 line-clamp-2">{panel.description}</p>
            )}
            {markers.length > 0 && (
              <div className="mt-1.5">
                <div className="flex flex-wrap gap-1">
                  {markers.slice(0, expanded ? markers.length : 4).map((marker, idx) => (
                    <Badge key={idx} variant="secondary" className="text-[10px] px-1.5 py-0">
                      {marker.name}
                    </Badge>
                  ))}
                  {!expanded && markers.length > 4 && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setExpanded(true);
                      }}
                      className="cursor-pointer"
                    >
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        +{markers.length - 4} more
                      </Badge>
                    </button>
                  )}
                  {expanded && markers.length > 4 && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setExpanded(false);
                      }}
                      className="cursor-pointer"
                    >
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        Show less
                      </Badge>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            {price != null && (
              <div className="flex items-center gap-0.5 text-base font-semibold text-emerald-600">
                <DollarSign className="h-3.5 w-3.5" />
                {Number(price).toFixed(0)}
              </div>
            )}
            <Button
              size="sm"
              className="mt-2 h-7 text-xs"
              onClick={() => onSelect(panel)}
            >
              {isSelected ? 'Selected' : 'Select'}
              <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PanelBrowserDialog;
